import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeft, Sparkles } from "lucide-react";
import { type ComponentType, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { api } from "../api/client";
import { k, t } from "../i18n/i18n";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { Motion, motion, Presence, useLastDefined, usePresence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { useMediaQuery } from "../components/useMediaQuery";
import { currentHelpScreen } from "../help/HelpButton";
import { HelpCenter } from "../help/HelpCenter";
import { HelpOnboarding } from "../help/HelpOnboarding";
import { showReferenceEnemies } from "../help/topics";
import { useReferenceEnemy } from "../enemyDisplay";
import { useHelp } from "../help/helpStore";
import { LayersPanel } from "../map/LayersPanel";
import { MapCanvas } from "../map/MapCanvas";
import { UnitContextMenu } from "../map/units/UnitContextMenu";
import { AssistantPanel } from "../panels/AssistantPanel";
import { Inspector } from "../panels/Inspector";
import { MissionMenuHost } from "../panels/MissionMenu";
import { type ViewKey, useUi } from "../state/ui";
import { GraphicsPanel } from "../views/GraphicsPanel";
import { AnnotationsPanel } from "../views/AnnotationsPanel";
import { AnnotationMenu } from "../map/annotations/AnnotationMenu";
import { TerrainPanel } from "../views/TerrainPanel";
import { MissionSummary } from "../views/MissionSummary";
import { displayedDossier, storedDraftFor } from "../views/missionDraft";
import { ManoeuvrePanel } from "../views/manoeuvre/ManoeuvrePanel";
import { OrbatPanel } from "../views/OrbatPanel";
import { BriefingView } from "../views/briefing/BriefingView";
import { CountryView } from "../views/country/CountryView";
import { AarView } from "../views/aar/AarView";
import { CoaView } from "../views/coa/CoaView";
import { DirexView } from "../views/direx/DirexView";
import { ExportsPanel } from "../views/exports/ExportsPanel";
import { LogisticsPanel } from "../views/logistics/LogisticsPanel";
import { MaintenancePanel } from "../views/maintenance/MaintenancePanel";
import { CoherencePanel } from "../views/coherence/CoherencePanel";
import { OplanView, OrderView } from "../views/order/OrderView";
import { OrbatsView } from "../views/orbats/OrbatsView";
import { CustomEnemiesView } from "../views/customEnemy/CustomEnemiesView";
import { DeclinationView } from "../views/declination/DeclinationView";
import { ReceivedOrdersView } from "../views/received/ReceivedOrdersView";
import { RetexView } from "../views/retex/RetexView";
import { DossierGenesisHost } from "./DossierGenesisCard";
import { FeedbackAnnounce } from "../feedback/FeedbackAnnounce";
import { FeedbackHost } from "../feedback/FeedbackHost";
import { ReportLinks } from "../feedback/reportLink";
import { CommandPalette } from "./CommandPalette";
import { WelcomeScreen } from "./home/WelcomeScreen";
import { leftColumnLayout, selectionKey } from "./mapColumns";
import { Sidebar } from "./Sidebar";
import { VersionNotice } from "./VersionNotice";
import { ReadOnlyHost } from "../licence/ReadOnlyLicence";
import { ShareDialogHost, useDossierAccessHost } from "../sharing/ReadOnlyBanner";
import { StorageDialogHost } from "../licence/StorageGauge";
import { useZoneWatch } from "../map/zones/useZones";

/** Camp et dispositif affichés par la carte quand les Données initiales (qui publient ceux de leur brouillon) ne
 *  sont pas ouvertes : relance de l'application sur l'ordre de bataille ou l'OPO, ouverture d'un autre dossier
 *  depuis l'accueil (`setExercise` remet le dispositif à zéro), « Reprendre », camp changé par un autre poste. Ils se
 *  déduisent du dossier ouvert, ou du brouillon gardé sur ce poste qui le modifie (`displayedDossier`) : jamais un
 *  camp mémorisé à part, jamais le décor d'un camp avec les unités d'un autre. Avec un brouillon, les écritures de
 *  la carte restent bloquées après un rechargement : le geste désigne « Enregistrer ». La météo du même dossier
 *  affiché suit (mesure au survol, analyse du terrain). */
function useDossierDisplay(view: string) {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const mapSlug = useUi((s) => s.mapSlug);
  const setDeploymentParams = useUi((s) => s.setDeploymentParams);
  const setWeatherKey = useUi((s) => s.setWeatherKey);
  const setMapSlug = useUi((s) => s.setMapSlug);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const current = exercises?.find((e) => e.id === exerciseId);
  useEffect(() => {
    if (view === "mission" || !current) return;
    const shown = displayedDossier(current, storedDraftFor(current));
    if (shown.mapSlug !== mapSlug) setMapSlug(shown.mapSlug);
    setDeploymentParams(shown.params);
    setWeatherKey(shown.weather);
  }, [view, current, params, mapSlug, setDeploymentParams, setWeatherKey, setMapSlug]);
}

/** Vues plein cadre au-dessus de la carte (qui reste montée) : documents, ordres de bataille, DIREX, 3A… Chacune
 *  entre et sort en fondu (`Presence`), un changement de vue n'est jamais sec. */
const FULL_VIEWS: { view: ViewKey; area: string; View: ComponentType }[] = [
  // Dossier d'exercice : documents plein cadre, comme l'ordre.
  { view: "country", area: k("la fiche pays"), View: CountryView },
  { view: "briefing", area: k("le dossier d'exercice"), View: BriefingView },
  // Ordres de bataille construits : indépendants du dossier ouvert.
  { view: "orbats", area: k("les ordres de bataille"), View: OrbatsView },
  // ENI personnalisés de la licence (registre A135) : fiche doctrinale, modes d'action, modèle de l'ENI.
  { view: "enemies", area: k("les ENI personnalisés"), View: CustomEnemiesView },
  { view: "received", area: k("les ordres reçus"), View: ReceivedOrdersView },
  // Ordres des subordonnés d'une brigade ou d'une division, réunis dans un document (docs/20 §13).
  { view: "declination", area: k("les ordres subordonnés"), View: DeclinationView },
  { view: "retex", area: k("le RETEX"), View: RetexView },
  { view: "evaluation", area: k("la 3A"), View: AarView },
  { view: "timeline", area: k("la DIREX"), View: DirexView },
  // Modes d'action ennemis.
  { view: "coa", area: k("les modes d'action"), View: CoaView },
  { view: "order", area: k("l'ordre d'opérations"), View: OrderView },
  // OPLAN du corps d'armée (docs/08 §25) : même vue que l'OPO, document n° 0 du dossier.
  { view: "oplan", area: k("le plan d'opération"), View: OplanView },
];

/** Largeur d'un élément, suivie au redimensionnement (0 tant qu'il n'est pas mesuré). */
function useWidth(ref: { current: HTMLElement | null }): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(Math.round(element.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** Clés des fiches affichées à droite : la fiche courante et les deux précédentes, gardées le temps de leur sortie
 *  en glissé (une `Presence` par objet sélectionné). */
function useCardKeys(current: string | null): string[] {
  const [keys, setKeys] = useState<string[]>(current ? [current] : []);
  if (current && !keys.includes(current)) setKeys([...keys.slice(-2), current]);
  return keys;
}

/** Coque de l'application : barre latérale, carte plein écran, panneaux flottants (ergonomie de Prevail). */
export function App() {
  const view = useUi((s) => s.view);
  const objectsFilter = useUi((s) => s.objectsFilter);
  const welcome = useUi((s) => s.welcome);
  useDossierDisplay(view);
  // Aide : fiches des ennemis de référence seulement dans le contexte d'une force française (registre B39). Posé
  // pendant le rendu, avant le centre d'aide et la recherche qui relisent les fiches (état de module idempotent).
  showReferenceEnemies(useReferenceEnemy());
  // Comptes nominatifs (docs/17 §3.4, §8.2) : dossier ouvert posé pour le client de l'API, refus des droits traités.
  useDossierAccessHost();
  // Zone libre du dossier ouvert : relevée pendant sa préparation où que soit l'utilisateur (docs/14 §2.7.5).
  useZoneWatch();
  const selection = useUi((s) => s.selection);
  const assistantOpen = useUi((s) => s.assistantOpen);
  const toggleAssistant = useUi((s) => s.toggleAssistant);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Arbitrage des colonnes (R16) : la colonne gauche s'empile ou se retire selon la place que laisse la droite.
  const mainRef = useRef<HTMLElement>(null);
  const mainWidth = useWidth(mainRef);
  const narrow = useMediaQuery("(max-width: 1023.98px)");
  const layersOpen = useUi((s) => s.layersOpen);
  const right = selection ? "card" : assistantOpen ? "assistant" : null;
  const columns = leftColumnLayout({ mainWidth, narrow, right, layersOpen });
  // Disposition affichée : celle d'avant le retrait, gardée pendant la sortie en glissé et tant que la colonne
  // est masquée (Calques ne sautent pas sous la vue au moment où elle s'en va).
  const shownColumns = useLastDefined(columns === "hidden" ? null : columns) ?? "row";
  // Colonne gauche retirée : elle glisse vers la gauche, puis se masque sans se démonter (le brouillon des
  // Données initiales et l'état des panneaux sont gardés) ; elle revient en glissé. Pas d'animation au premier
  // affichage : les panneaux ont la leur.
  const leftShown = columns !== "hidden";
  const left = usePresence(leftShown);
  const [leftMoved, setLeftMoved] = useState(false);
  if (!leftShown && !leftMoved) setLeftMoved(true);
  // Retour de la colonne : elle seule glisse ; ses panneaux, qui sortent de leur masquage, ne rejouent pas leur
  // propre entrée (durée nulle le temps du retour, sans changer d'animation : rien ne se rejoue ensuite).
  const [leftWasShown, setLeftWasShown] = useState(leftShown);
  const [returning, setReturning] = useState(false);
  if (leftWasShown !== leftShown) {
    setLeftWasShown(leftShown);
    setReturning(leftShown && leftMoved);
  }
  useEffect(() => {
    if (!returning) return;
    const timer = window.setTimeout(() => setReturning(false), 400);
    return () => window.clearTimeout(timer);
  }, [returning]);
  const cardKey = selection ? selectionKey(selection) : null;
  const cardKeys = useCardKeys(cardKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // La recherche porte sur l'espace de travail : pas depuis l'accueil.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !useUi.getState().welcome) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      // « ? » : aide de la vue affichée (pas pendant une saisie).
      const target = e.target as HTMLElement | null;
      const typing = target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      if (e.key === "?" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const help = useHelp.getState();
        if (help.open) help.closeHelp();
        else help.openHelp({ screen: currentHelpScreen() });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    // « Signaler cette anomalie » sous les messages d'erreur de l'espace de travail et de l'accueil (registre F39) ;
    // jamais sur l'écran d'activation ni de connexion, rendus sans `App`.
    <ReportLinks enabled>
      {/* « overflow-clip » et non « hidden » : un élément mis au point hors champ (tablette en portrait) ne peut
          plus faire défiler toute l'application de côté, barre latérale rognée. */}
      <div className="relative flex h-full w-full overflow-clip">
        <Sidebar onSearch={() => setPaletteOpen(true)} />
        <main ref={mainRef} className="relative min-w-0 flex-1">
          <MapCanvas />
          <OrderReturnChip />

          {/* Colonne gauche : vue courante, puis calques — côte à côte, empilés (Calques sous la vue) ou retirés selon
              la place que laisse la colonne droite (`leftColumnLayout`) : aucun panneau n'en recouvre un autre. Bas à
              72 px : l'échelle de la carte (coin bas gauche) garde 10 px sous le panneau. */}
          <div
            className={clsx(
              "pointer-events-none absolute top-3 bottom-18 left-3 z-10 flex gap-3",
              shownColumns === "row" ? "flex-row" : "flex-col",
              leftMoved && motion("left", left.closing),
              returning && "[&_[data-floating-panel]]:[animation-duration:0s]",
              !left.mounted && "hidden",
            )}
            inert={!leftShown}
          >
            <div className={clsx("flex max-h-full flex-col", shownColumns !== "row" && "min-h-0 flex-1")}>
              <ErrorBoundary area={k("cette vue")} resetKey={view} className="flex">
                {view === "mission" ? (
                  <MissionSummary />
                ) : view === "objects" ? (
                  objectsFilter === "graphics" ? (
                    <GraphicsPanel />
                  ) : objectsFilter === "annotations" ? (
                    <AnnotationsPanel />
                  ) : (
                    <OrbatPanel />
                  )
                ) : view === "manoeuvre" ? (
                  <ManoeuvrePanel />
                ) : view === "terrain" ? (
                  <TerrainPanel />
                ) : view === "exports" ? (
                  <ExportsPanel />
                ) : view === "logistics" ? (
                  <LogisticsPanel />
                ) : view === "maintenance" ? (
                  <MaintenancePanel />
                ) : view === "coherence" ? (
                  <CoherencePanel />
                ) : null}
              </ErrorBoundary>
            </div>
            <div className={clsx("flex max-h-full flex-col", shownColumns !== "row" && "max-h-[40%] shrink-0")}>
              {/* Calques : repliés à l'ouverture de l'application, dépliés d'un clic sur leur en-tête. */}
              <LayersPanel />
            </div>
          </div>

          {FULL_VIEWS.map(({ view: key, area, View }) => (
            <Presence key={key} show={view === key}>
              <Motion origin="fade" className="absolute inset-0 z-20 flex items-center justify-center bg-canvas/80">
                <ErrorBoundary area={area} className="flex h-full w-full items-center justify-center">
                  <View />
                </ErrorBoundary>
              </Motion>
            </Presence>
          ))}

          {/* Colonne droite : fiche d'objet, sinon assistant IA, sinon son bouton. Chacun entre et sort en glissé
              depuis le bord droit (une fiche par objet sélectionné) ; ils partagent la même case, celui qui sort
              passe sous celui qui entre. */}
          <div className="pointer-events-none absolute top-3 right-3 bottom-18 z-10 grid grid-rows-[minmax(0,1fr)] items-start justify-items-end *:col-start-1 *:row-start-1">
            {cardKeys.map((key) => (
              <Presence key={key} show={key === cardKey}>
                {key === cardKey && selection ? <RightSlot><Inspector selection={selection} /></RightSlot> : null}
              </Presence>
            ))}
            <Presence show={!selection && assistantOpen}>
              <RightSlot>
                <AssistantPanel />
              </RightSlot>
            </Presence>
            <Presence show={!selection && !assistantOpen}>
              {/* Bouton de l'assistant : 38 px, aligné sur les en-têtes de panneau (36 px et leur bordure). */}
              <Motion
                origin="zoom"
                data-floating-panel=""
                className="pointer-events-auto rounded-xl border border-line bg-surface p-0.5 shadow-float"
              >
                <IconButton onClick={toggleAssistant} title={t("Assistant IA")} aria-label={t("Assistant IA")}>
                  <Sparkles size={16} />
                </IconButton>
              </Motion>
            </Presence>
          </div>
        </main>
        <CommandPalette open={paletteOpen && !welcome} onClose={() => setPaletteOpen(false)} />
        {/* Menu d'une unité sur la carte (clic droit, appui long) et menu de mission : au-dessus de tout, un seul de
            chaque à la fois. */}
        {!welcome && <UnitContextMenu />}
        {!welcome && <AnnotationMenu />}
        {!welcome && <MissionMenuHost />}
        {/* Accueil : au-dessus de l'espace de travail, qui se charge derrière et se découvre à l'ouverture d'un
            dossier. */}
        {welcome && <WelcomeScreen />}
        {/* Aide en ligne et prise en main, au-dessus de tout (accueil compris). */}
        <HelpCenter />
        <StorageDialogHost />
        {/* Licence expirée en lecture seule : explication des actions refusées, fiche de la licence. */}
        <ReadOnlyHost />
        <ShareDialogHost inactive={welcome} />
        {/* Genèse du dossier d'exercice (registre F37) : carte en bas à droite, sans rien bloquer,
            accueil compris. */}
        <DossierGenesisHost />
        <HelpOnboarding />
        {/* Retours des utilisateurs (registre F39) : évolution, anomalie, mes retours, administration. */}
        <FeedbackHost />
        {/* Annonce du bouton « Retours », une fois par poste, quand rien d'autre ne demande l'attention. */}
        <FeedbackAnnounce />
        {/* Nouvelle version mise en ligne (audit m14) : avis en haut de l'écran, accueil compris. */}
        <VersionNotice />
      </div>
    </ReportLinks>
  );
}

/** Case de la colonne droite : le panneau y garde la hauteur de la colonne au plus (il défile au-delà). */
function RightSlot({ children }: { children: ReactNode }) {
  return <div className="flex max-h-full min-h-0 flex-col items-end">{children}</div>;
}

/** Carte ouverte depuis l'OPO sur une unité (09/10/2026) : « Retour à l'OPO » ramène à la lecture, au même
 *  endroit (de même pour la mise en ambiance et la fiche pays). Disparaît dès que l'utilisateur change lui-même de
 *  vue. */
function OrderReturnChip() {
  const view = useUi((s) => s.view);
  const orderReturn = useUi((s) => s.orderReturn);
  const backToOrder = useUi((s) => s.backToOrder);
  const shown = view === "objects" && orderReturn !== null;
  return (
    <Presence show={shown}>
      {shown && (
        <Motion origin="top" className="pointer-events-none absolute inset-x-0 top-3 z-30 flex justify-center">
          <Button className="pointer-events-auto shadow-lg" onClick={backToOrder} highlight>
            <ArrowLeft size={14} />{" "}
            {orderReturn?.view === "briefing"
              ? t("Retour à la mise en ambiance")
              : orderReturn?.view === "country"
                ? t("Retour à la fiche pays")
                : orderReturn?.view === "oplan"
                  ? t("Retour à l'OPLAN")
                  : t("Retour à l'OPO")}
          </Button>
        </Motion>
      )}
    </Presence>
  );
}
